// =============================================================================
// ChatbotPrefillService
//
// In-memory hand-off for chatbot create-flow prefill data, keyed by "kind"
// (member / task / group). The chatbot panel pushes a payload via request();
// the destination page reads + clears via consume(). A signal-based pendingKinds
// view lets pages react via effect() so the warm path (already on destination
// page) works the same as the cold path (fresh navigation).
//
// Why a service instead of URL query params:
// - Prefill payloads carry PII (email, phone, address). Putting them in the URL
//   leaks into browser history, server access logs, analytics, and referrer
//   headers. The service keeps everything in-memory.
//
// SK-compat: this lives entirely on the frontend; backend ships prefill as a
// dedicated DTO field, so a future SK adapter doesn't touch this code path.
//
// Mirror of MemboraApiConstants.AiAssistant.PrefillKinds on the backend.
// =============================================================================

import { Injectable, computed, signal } from '@angular/core';
import { type ChatbotPrefillKindCode } from '../constants';

// Values may be string OR string[]. Arrays land here when a multi-select
// slot-fill chip group has 2+ selections (e.g. capacityType = ["MEMBER",
// "VOLUNTEER"]). Single-select chips and LLM-extracted values stay string.
export type ChatbotPrefillValue = string | string[];
export type ChatbotPrefillData = Record<string, ChatbotPrefillValue>;

type PrefillBuffer = Partial<Record<ChatbotPrefillKindCode, ChatbotPrefillData>>;

@Injectable({ providedIn: 'root' })
export class ChatbotPrefillService {
  private readonly buffer = signal<PrefillBuffer>({});

  /** Reactive set of kinds with a pending payload. Pages effect() on this. */
  readonly pendingKinds = computed(() => Object.keys(this.buffer()) as ChatbotPrefillKindCode[]);

  /** Chatbot panel calls this before router.navigateByUrl. */
  request(kind: ChatbotPrefillKindCode, data: ChatbotPrefillData = {}): void {
    this.buffer.update(current => ({ ...current, [kind]: { ...data } }));
  }

  /** Destination page reads + clears its slot. Returns null when empty. */
  consume(kind: ChatbotPrefillKindCode): ChatbotPrefillData | null {
    const current = this.buffer();
    if (!(kind in current)) return null;
    const data = current[kind]!;
    const rest: PrefillBuffer = { ...current };
    delete rest[kind];
    this.buffer.set(rest);
    return data;
  }

  /** Non-clearing peek for predicate-style reads inside an effect(). */
  has(kind: ChatbotPrefillKindCode): boolean {
    return kind in this.buffer();
  }
}
